import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Button,
  Dropdown,
} from '@lockerpm/design';

import {
  LogoutOutlined,
  LockOutlined,
  GroupOutlined,
  MessageOutlined,
  CustomerServiceOutlined,
  FlagOutlined,
  QuestionCircleOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';

import commonComponents from '../common';

import authServices from "../../services/auth";
import userServices from '../../services/user';

import global from '../../config/global';
import common from '../../utils/common';
import storeActions from '../../store/actions';

function DropdownMenu() {
  const { UserAvatar } = commonComponents;
  const { t } = useTranslation();

  const isMobile = useSelector((state) => state.system.isMobile);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const teams = useSelector((state) => state.enterprise.teams);
  const tutorialRunning = useSelector((state) => state.onboarding.tutorialRunning);
  const stepIndex = useSelector((state) => state.onboarding.stepIndex);


  const isEnterpriseAdmin = useMemo(() => {
    if (teams?.length > 0) {
      return teams[0]?.role?.includes('admin') && common.isEnterpriseMember()
    }
    return false
  }, [teams, userInfo])

  const dropdownClick = async (item) => {
    if (item.key === 'account') {
      global.navigate(global.keys.SETTINGS_ACCOUNT)
    } else if (item.key === 'logout') {
      authServices.logout(true);
    } else if (item.key === 'lock') {
      await authServices.lock();
    } else if (item.key === 'enterprise') {
      common.openNewTab(`${global.urls.LOCKER_ENTERPRISE_URL}`)
    } else if (item.key === 'support_center') {
      common.openNewTab(`${global.urls.SUPPORT_CENTER_URL}`)
    } else if (item.key === 'give_feedback') {
      common.openNewTab(`${global.urls.GIVE_FEEDBACK_URL}`)
    } else if (item.key === 'report_bugs') {
      common.openNewTab(`${global.urls.REPORT_BUGS_URL}`)
    } else if (item.key === 'welcome_tour') {
      global.navigate(global.keys.WELCOME_TOUR)
    } else if (item.key === 'get_extension') {
      common.openNewTab(`${global.urls.EXTENSION_DOWNLOAD}`)
    } else if (
      Object.values(global.constants.LANGUAGE).includes(item.key)
    ) {
      updateLanguage(item.key)
    }
  }

  const updateLanguage = (value) => {
    const payload = {
      customer_language: value
    }
    userServices.update_me(payload).then(() => {
      common.fetchUserInfo();
    }).catch((error) => {
      global.pushError(error)
    })
  }

  const handleTutorialRunning = async () => {
    if (tutorialRunning && stepIndex === 4) {
      await common.updateOnboardingProcess('view_profile');
      global.store.dispatch(storeActions.updateTutorialRunning(false))
      setTimeout(() => {
        global.store.dispatch(storeActions.updateStepIndex(0));
        global.store.dispatch(storeActions.updateGetExtensionVisible(true));
      }, 100);
    }
  }

  return (
    <Dropdown
      menu={{
        items: [
          {
            key: 'account',
            icon: <UserAvatar />,
            label: <></>,
          },
          {
            key: 'language',
            label: <span>
              <span className='font-semibold'>
                {t('common.language')}:</span> <span>
                {t(global.constants.LANGUAGES.find((l) => l.value == userInfo?.customer_language).label)}
              </span>
            </span>,
            children: global.constants.LANGUAGES.map((l) => ({
              key: l.value,
              label: t(l.label),
            }))
          },
          {
            key: 'enterprise',
            icon: <GroupOutlined />,
            label: <span>{t('sidebar.enterprise')}</span>,
            hide: !(userInfo.is_super_admin || isEnterpriseAdmin)
          },
          {
            key: 'support_center',
            icon: <CustomerServiceOutlined />,
            label: <span>{t('sidebar.support_center')}</span>
          },
          {
            key: 'give_feedback',
            icon: <MessageOutlined />,
            label: <span>{t('sidebar.give_feedback')}</span>
          },
          {
            key: 'report_bugs',
            icon: <QuestionCircleOutlined />,
            label: <span>{t('sidebar.report_bugs')}</span>
          },
          {
            key: 'welcome_tour',
            icon: <FlagOutlined />,
            label: <span>{t('sidebar.welcome_tour')}</span>
          },
          {
            key: 'get_extension',
            icon: <ArrowRightOutlined />,
            label: <span>{t('sidebar.get_extension')}</span>
          },
          {
            type: 'divider',
          },
          {
            key: 'lock',
            icon: <LockOutlined />,
            label: <span>{t('sidebar.lock_your_account')}</span>
          },
          {
            key: 'logout',
            danger: true,
            icon: <LogoutOutlined />,
            label: <span>{t('sidebar.logout')}</span>
          },
        ].filter((i) => !i.hide).map((i) => { delete i.hide; return i }),
        defaultSelectedKeys: [],
        onClick: dropdownClick
      }}
      placement="bottomLeft"
      trigger={'click'}
    >
      <div
        className='user-info flex item-center justify-center'
        onClick={() => handleTutorialRunning()}
      >
        {
          !isMobile && <Button
            type='text'
            className='avatar-button'
            shape='circle'
            icon={<UserAvatar showInfo={false}/>}
          />
        }
        {
          isMobile && <div className='cursor-pointer'>
            <UserAvatar showInfo={false}/>
          </div>
        }
      </div>
    </Dropdown>
  );
}

export default DropdownMenu;
